/**
 * Seed list of well-known companies that grant RSUs or equity, used to auto-populate
 * `org.is_rsu_granting` and `org.is_equity_granting` on new / renamed org rows.
 *
 * Source of truth for both the one-time migration
 * (packages/types/migrations/1777300000000_add_equity_flags_to_org.js) and the
 * ongoing `orgSync` hook that re-applies the match when an org's `primary_name`
 * changes. The migration's inline copy is a frozen historical snapshot; update
 * this file going forward and, if retroactive backfill is required, write a
 * follow-up migration that shares the same list.
 */

/**
 * Public companies with standard RSU programs. Membership implies BOTH
 * `is_rsu_granting` and `is_equity_granting` true.
 */
export const RSU_GRANTING_ORG_NAMES: readonly string[] = [
  // Big tech
  'alphabet', 'alphabet inc', 'alphabet inc.', 'google', 'google llc', 'google inc',
  'meta', 'meta platforms', 'meta platforms inc', 'facebook',
  'amazon', 'amazon.com', 'amazon web services', 'aws',
  'apple', 'apple inc', 'apple inc.',
  'microsoft', 'microsoft corporation',
  'netflix', 'netflix inc',
  'nvidia', 'nvidia corporation',
  'tesla', 'tesla inc', 'tesla motors',
  'oracle', 'oracle corporation',
  'salesforce', 'salesforce.com', 'salesforce inc',
  'adobe', 'adobe inc', 'adobe systems',
  'intel', 'intel corporation',
  'cisco', 'cisco systems',
  'ibm', 'international business machines',
  'linkedin', 'linkedin corporation',
  'dell', 'dell technologies',
  'hp', 'hp inc', 'hewlett packard', 'hewlett-packard',
  'hpe', 'hewlett packard enterprise',
  'vmware',
  'workday',
  'servicenow',
  'twilio',
  'zoom', 'zoom video communications',
  // Newer public tech
  'uber', 'uber technologies',
  'airbnb', 'airbnb inc',
  'lyft',
  'snap', 'snap inc', 'snapchat',
  'pinterest',
  'spotify',
  'shopify',
  'block', 'block inc', 'square',
  'paypal', 'paypal holdings',
  'palantir', 'palantir technologies',
  'snowflake',
  'datadog',
  'mongodb',
  'cloudflare',
  'coinbase',
  'atlassian',
  'asana',
  'doordash',
  'instacart', 'maplebear',
  'robinhood', 'robinhood markets',
  'roblox',
  'unity', 'unity technologies', 'unity software',
  'gitlab',
  'hashicorp',
  'confluent',
  'okta',
  'crowdstrike',
  'zscaler',
  'elastic',
  'fastly',
  'twitter', 'x corp', 'x.com',
  // Banking / finance
  'goldman sachs', 'goldman sachs group',
  'jpmorgan', 'jpmorgan chase', 'jp morgan', 'j.p. morgan',
  'morgan stanley',
  'blackrock',
  'bank of america',
  'wells fargo',
  'citigroup', 'citi',
  'american express',
  'visa',
  'mastercard',
];

/**
 * Private / late-stage companies that grant equity (usually options, pre-IPO
 * stock) but not RSUs. Membership implies `is_equity_granting` only.
 */
export const EQUITY_GRANTING_ONLY_ORG_NAMES: readonly string[] = [
  'stripe',
  'databricks',
  'openai',
  'anthropic',
  'notion', 'notion labs',
  'figma',
  'canva',
  'scale', 'scale ai',
  'ramp',
  'brex',
  'mercury',
  'retool',
  'vercel',
  'supabase',
  'linear',
  'perplexity', 'perplexity ai',
  'cursor', 'anysphere',
];

const RSU_SET = new Set(RSU_GRANTING_ORG_NAMES);
const EQUITY_ONLY_SET = new Set(EQUITY_GRANTING_ONLY_ORG_NAMES);

function normalize(name: string | null | undefined): string | null {
  if (name == null) return null;
  const trimmed = name.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/** True if the org name matches a known RSU-granting public company. Case-insensitive. */
export function isRsuGrantingOrgName(name: string | null | undefined): boolean {
  const n = normalize(name);
  return n != null && RSU_SET.has(n);
}

/** True if the org grants equity in any form (RSUs imply equity). Case-insensitive. */
export function isEquityGrantingOrgName(name: string | null | undefined): boolean {
  const n = normalize(name);
  return n != null && (RSU_SET.has(n) || EQUITY_ONLY_SET.has(n));
}
